import React from 'react';
import { AlertTriangle, RefreshCcw, RotateCcw } from 'lucide-react';
import Card from './ui/Card';
import Button from './ui/Button';

const subtleTextStyle = {
  fontSize: '0.82rem',
  color: 'var(--text-secondary)',
  lineHeight: 1.5,
};

const AppErrorState = ({
  title = '页面加载失败',
  message = '',
  error = null,
  compact = false,
  fullscreen = false,
  minHeight = '280px',
  onRetry,
  onReload,
  retryLabel = '重试',
  reloadLabel = '刷新应用',
  children,
}) => {
  const resolvedMessage =
    String(
      message || error?.appDisplayMessage || error?.message || ''
    ).trim() || '页面初始化时发生异常';
  const detail = String(error?.appDetail || '').trim();
  const detailLines = [
    error?.appStage ? `阶段：${error.appStage}` : '',
    error?.appPath ? `路径：${error.appPath}` : '',
    error?.appEndpoint ? `接口：${error.appEndpoint}` : '',
    error?.appKind ? `类型：${error.appKind}` : '',
    error?.appScope ? `范围：${error.appScope}` : '',
    error?.appResource ? `资源：${error.appResource}` : '',
  ].filter(Boolean);

  const content = (
    <Card
      padding={compact ? '1rem' : '1.25rem'}
      style={{
        width: '100%',
        maxWidth: fullscreen ? '720px' : '100%',
        border: '1px solid rgba(239, 68, 68, 0.16)',
        backgroundColor: 'var(--bg-primary)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.85rem',
        }}
      >
        <div
          style={{
            width: compact ? '2.25rem' : '2.75rem',
            height: compact ? '2.25rem' : '2.75rem',
            borderRadius: '9999px',
            flex: '0 0 auto',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(239, 68, 68, 0.12)',
            color: 'var(--error)',
          }}
        >
          <AlertTriangle size={compact ? 18 : 20} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              color: 'var(--text-primary)',
              fontSize: compact ? '0.98rem' : '1.08rem',
            }}
          >
            {title}
          </div>
          <div
            style={{
              marginTop: '0.35rem',
              color: 'var(--text-secondary)',
              lineHeight: 1.55,
              fontSize: compact ? '0.88rem' : '0.92rem',
            }}
          >
            {resolvedMessage}
          </div>
          {detailLines.length > 0 && (
            <div style={{ marginTop: '0.6rem', ...subtleTextStyle }}>
              {detailLines.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </div>
          )}
          {detail && detail !== resolvedMessage && (
            <div
              style={{
                marginTop: '0.6rem',
                padding: '0.55rem 0.65rem',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'rgba(148, 163, 184, 0.08)',
                ...subtleTextStyle,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {detail}
            </div>
          )}
          {children}
          <div
            style={{
              marginTop: '0.85rem',
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
            }}
          >
            {typeof onRetry === 'function' && (
              <Button variant='primary' onClick={onRetry} icon={RefreshCcw}>
                {retryLabel}
              </Button>
            )}
            {typeof onReload === 'function' && (
              <Button variant='outline' onClick={onReload} icon={RotateCcw}>
                {reloadLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );

  if (!fullscreen) {
    return content;
  }

  return (
    <div
      style={{
        minHeight,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.25rem',
        boxSizing: 'border-box',
      }}
    >
      {content}
    </div>
  );
};

export default AppErrorState;
